import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Grid from 'apollo-react/components/Grid';
import Divider from 'apollo-react/components/Divider';
import Typography from 'apollo-react/components/Typography';
import TextField from 'apollo-react/components/TextField';
import Tooltip from 'apollo-react/components/Tooltip';
import Info from 'apollo-react-icons/Info';
import Switch from 'apollo-react/components/Switch';
import ButtonCommon from '../../common/Button';
import useProviderDetail from './hooks/useProviderDetail';
import { saveProviderConfig } from './paymentApiService';
import SecretInputField from './components/SecretInputField';
import OutputFieldRow from './components/OutputFieldRow';

const ProviderManage = () => {
  const { code } = useParams();
  const [t] = useTranslation();
  const { detail, loading, error, reload } = useProviderDetail(code);

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [activateOnSave, setActivateOnSave] = useState(false);

  useEffect(() => {
    if (detail?.input_fields) {
      const values = {};
      for (const field of detail.input_fields) {
        if (!field.sensitive) {
          values[field.key] = field.value || '';
        }
      }
      setFormValues(values);
      setActivateOnSave(detail.provider?.is_active || false);
    }
  }, [detail]);

  const handleFieldChange = useCallback((key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleCancel = useCallback(() => {
    if (detail?.input_fields) {
      const values = {};
      for (const field of detail.input_fields) {
        if (!field.sensitive) {
          values[field.key] = field.value || '';
        }
      }
      setFormValues(values);
    }
    setFieldErrors({});
    setEditMode(false);
    setActivateOnSave(detail?.provider?.is_active || false);
  }, [detail]);

  const handleSave = async () => {
    setSaving(true);
    setFieldErrors({});

    try {
      const payload = {};
      for (const field of detail.input_fields) {
        const val = formValues[field.key];
        payload[field.key] = typeof val === 'string' ? val : '';
      }

      await saveProviderConfig(code, payload, activateOnSave);
      toast.success(t('Provider configuration saved successfully'), {
        position: 'top-center',
      });
      setEditMode(false);
      reload();
    } catch (err) {
      const errorData = err.response?.data?.error;
      if (errorData?.details && typeof errorData.details === 'object') {
        setFieldErrors(errorData.details);
        toast.error('Please fix the highlighted fields and try again.', {
          position: 'top-center',
        });
      } else {
        const status = err.response?.status;
        const message =
          status >= 500
            ? 'Something went wrong on the server. Please contact the administrator.'
            : errorData?.message || 'Save failed. Please try again.';
        toast.error(message, { position: 'top-center' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Typography>{t('loading')}...</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  if (!detail) return null;

  const { provider, input_fields, output_fields, has_implementation } = detail;

  const renderFieldLabel = (field) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span>{field.label}</span>
      {field.required && (
        <span style={{ color: '#d32f2f', fontSize: 20, lineHeight: '14px' }}>
          *
        </span>
      )}
      {field.help_text && (
        <Tooltip title={field.help_text} placement="top">
          <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
            <Info fontSize="extraSmall" style={{ color: '#595959' }} />
          </span>
        </Tooltip>
      )}
    </div>
  );

  return (
    <div
      className="content"
      style={{
        border: 'solid 1px #E9E9E9',
        padding: '24px',
        borderRadius: '5px',
      }}
    >
      <Typography variant="h2" style={{ marginBottom: 8 }}>
        {provider.display_name}
      </Typography>

      {provider.description && (
        <Typography variant="body1" style={{ marginBottom: 24, color: '#666' }}>
          {provider.description}
        </Typography>
      )}

      {!has_implementation && (
        <Typography color="error" style={{ marginBottom: 24 }}>
          Provider implementation is not available in this deployment.
        </Typography>
      )}

      {has_implementation && (
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={12}>
            <Typography
              variant="h3"
              style={{ marginBottom: 16, marginTop: 16 }}
            >
              {t('Input Fields')}
            </Typography>

            {input_fields.map((field) =>
              field.sensitive ? (
                <SecretInputField
                  key={field.key}
                  field={field}
                  value={formValues[field.key]}
                  onChange={handleFieldChange}
                  disabled={!editMode}
                  error={fieldErrors[field.key]}
                />
              ) : (
                <div key={field.key} style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6 }}>
                    {renderFieldLabel(field)}
                  </div>
                  <TextField
                    value={
                      editMode
                        ? (formValues[field.key] ?? '')
                        : field.value || ''
                    }
                    onChange={(e) =>
                      handleFieldChange(field.key, e.target.value)
                    }
                    disabled={!editMode}
                    error={!!fieldErrors[field.key]}
                    helperText={fieldErrors[field.key] || ''}
                    fullWidth
                  />
                </div>
              )
            )}

            <Switch
              label={t('Activate Provider')}
              checked={activateOnSave}
              onChange={(e, checked) => setActivateOnSave(checked)}
              disabled={!editMode}
              size="small"
              style={{ marginTop: 16, marginBottom: 8 }}
            />

            <div style={{ textAlign: 'right', marginTop: 32 }}>
              {!editMode ? (
                <ButtonCommon
                  color="primary"
                  text={t('edit')}
                  onClick={() => setEditMode(true)}
                  style={{
                    minWidth: 100,
                    minHeight: 32,
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                />
              ) : (
                <>
                  <ButtonCommon
                    color="secondary"
                    text={t('cancel')}
                    onClick={handleCancel}
                    style={{
                      minWidth: 100,
                      minHeight: 32,
                      fontSize: 14,
                      fontWeight: 600,
                      marginRight: 8,
                    }}
                  />
                  <ButtonCommon
                    color="primary"
                    text={saving ? `${t('saving')}...` : t('Save')}
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      minWidth: 100,
                      minHeight: 32,
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  />
                </>
              )}
            </div>

            {output_fields.length > 0 && (
              <>
                <Divider style={{ marginTop: 32, marginBottom: 24 }} />
                <Typography variant="h3" style={{ marginBottom: 16 }}>
                  {t('Output Fields')}
                </Typography>
                <Typography
                  variant="body2"
                  style={{ marginBottom: 16, color: '#666' }}
                >
                  Copy these values to your provider dashboard.
                </Typography>
                {output_fields.map((field) => (
                  <OutputFieldRow key={field.key} field={field} />
                ))}
              </>
            )}
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default ProviderManage;
